import api from '../utils/api';

/**
 * @typedef {Object} Trade
 * @property {string} _id
 * @property {string} name
 * @property {string} slug
 * @property {string} description
 * @property {string} icon
 * @property {string} thumbnail
 * @property {boolean} isActive
 * @property {number} order
 * @property {string} metaTitle
 * @property {string} metaDescription
 * @property {string[]} keywords
 * @property {number} coursesCount
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} CreateTradeData
 * @property {string} name
 * @property {string} [description]
 * @property {string} [icon]
 * @property {string} [thumbnail]
 * @property {number} [order]
 * @property {string} [metaTitle]
 * @property {string} [metaDescription]
 * @property {string[]} [keywords]
 */

export const tradeService = {
  // ==================
  // PUBLIC ROUTES
  // ==================

  /**
   * Get all trades
   * @param {Object} [params]
   * @param {boolean} [params.isActive]
   * @param {string} [params.search]
   * @param {string} [params.sort]
   * @returns {Promise<{status: string, count: number, trades: Trade[]}>}
   */
  getAllTrades: async (params = {}) => {
    const response = await api.get('/api/trades', { params });
    return response;
  },

  /**
   * Get trade by ID
   * @param {string} id 
   * @returns {Promise<{status: string, trade: Trade}>}
   */
  getTradeById: async (id) => {
    const response = await api.get(`/api/trades/${id}`);
    return response;
  },

  /**
   * Get trade by slug
   * @param {string} slug 
   * @returns {Promise<{status: string, trade: Trade}>}
   */
  getTradeBySlug: async (slug) => {
    const response = await api.get(`/api/trades/slug/${slug}`);
    return response;
  },

  /**
   * Get courses by trade
   * @param {string} id 
   * @param {Object} [params]
   * @returns {Promise<{status: string, trade: Object, count: number, courses: Object[]}>}
   */
  getCoursesByTrade: async (id, params = {}) => {
    const response = await api.get(`/api/trades/${id}/courses`, { params });
    return response;
  },

  // ==================
  // ADMIN ROUTES
  // ==================

  /**
   * Create trade (Admin)
   * @param {CreateTradeData} tradeData 
   * @returns {Promise<{status: string, trade: Trade}>}
   */
  createTrade: async (tradeData) => {
    const response = await api.post('/api/trades', tradeData);
    return response;
  },

  /**
   * Update trade (Admin)
   * @param {string} id 
   * @param {Object} updates 
   * @returns {Promise<{status: string, trade: Trade}>}
   */
  updateTrade: async (id, updates) => {
    const response = await api.put(`/api/trades/${id}`, updates);
    return response;
  },

  /**
   * Delete trade (Admin)
   * @param {string} id 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteTrade: async (id) => {
    const response = await api.delete(`/api/trades/${id}`);
    return response;
  },

  /**
   * Toggle trade active status (Admin)
   * @param {string} id 
   * @returns {Promise<{status: string, message: string, trade: Trade}>}
   */
  toggleTradeActive: async (id) => {
    const response = await api.patch(`/api/trades/${id}/toggle-active`);
    return response;
  },

  /**
   * Get trade statistics (Admin)
   * @param {string} id 
   * @returns {Promise<{status: string, trade: Object, statistics: Object}>}
   */
  getTradeStatistics: async (id) => {
    const response = await api.get(`/api/trades/${id}/statistics`);
    return response;
  },
};

